import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Depths, Stack, Text } from '@fluentui/react';
import { useOrganizationBranding } from 'features/Header/hooks';
import { getTextColorForBackground, IvicosColors } from 'kits/colorKit';
import IvicosLogo from 'media/logos/iv_campus_logo_white.svg';
import { idpService } from 'services/api/identity-provider.service';
import { IC } from 'components/SVGIcon';
import { FeazyChat } from 'components/FeazyChat';
import { Languages } from 'lucide-react';

type UiLang = 'en' | 'de';

/** Payload from link-evaluation app when embedded (see access-management link-evaluation page). */
export interface ILinkEvaluationChatbotPayload {
    is_chat_bot_added: boolean;
    chat_bot_prompt_id: string;
    chat_bot_base_url: string;
    embedUrl: string | null;
    showForClosedAccess: boolean;
}

export interface ILinkEvaluationParentPayload {
    type: 'LINK_EVALUATION';
    isValid: boolean;
    isCurrentlyOpen: boolean;
    chatbot: ILinkEvaluationChatbotPayload | null;
    slug?: string;
    /** Sensitive — avoid logging */
    token?: string;
    campusId?: string;
    orgId?: string;
    currentStatus?: {
        message: string;
        reason: string;
        nextOpenTime?: string;
    };
}

export interface ILinkIsValidParentPayload {
    type: 'LINK_IS_VALID';
    data: unknown;
}

function isLinkEvaluationMessage(data: unknown): data is ILinkEvaluationParentPayload {
    return typeof data === 'object' && data !== null && (data as { type?: string }).type === 'LINK_EVALUATION';
}

function isLinkIsValidMessage(data: unknown): data is ILinkIsValidParentPayload {
    return typeof data === 'object' && data !== null && (data as { type?: string }).type === 'LINK_IS_VALID';
}

function optionalNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function unwrapAccessPassPayload(payload: unknown): unknown {
    if (!payload || typeof payload !== 'object') return payload;
    const record = payload as Record<string, unknown>;

    if (record.data && typeof record.data === 'object') {
        const nestedData = record.data as Record<string, unknown>;
        if (nestedData.accessPass) return nestedData.accessPass;
        if (nestedData.access_pass) return nestedData.access_pass;
    }

    if (record.accessPass) return record.accessPass;
    if (record.access_pass) return record.access_pass;

    return payload;
}

/** Feazy config embedded in GET `/v1/access_passes/slug/:slug` (not CRA env vars). */
function getFeazyEmbedFromSlugAccessPass(pass: unknown): { promptId: string; baseUrl?: string } | null {
    const accessPass = unwrapAccessPassPayload(pass);
    if (!accessPass || typeof accessPass !== 'object') return null;
    const record = accessPass as Record<string, unknown>;
    const promptId =
        optionalNonEmptyString(record.chat_bot_prompt_id) ?? optionalNonEmptyString(record.chatBotPromptId) ?? optionalNonEmptyString(record.feazy_prompt_id);
    if (!promptId) return null;
    const baseUrl =
        optionalNonEmptyString(record.chat_bot_base_url) ?? optionalNonEmptyString(record.chatBotBaseUrl) ?? optionalNonEmptyString(record.feazy_base_url);
    if (typeof record.is_chat_bot_added === 'boolean' && !record.is_chat_bot_added) return null;
    return { promptId, baseUrl };
}

function readStoredInterfaceLanguage(): UiLang | null {
    const raw = localStorage.getItem('UserLanguageChoice') || localStorage.getItem('chosenLanguage');
    return raw === 'en' || raw === 'de' ? raw : null;
}

function readBrowserInterfaceLanguage(): UiLang {
    if (typeof navigator === 'undefined') return 'de';
    return navigator.language?.includes('de') ? 'de' : 'en';
}

function getInitialInterfaceLanguage(): UiLang {
    return readStoredInterfaceLanguage() || readBrowserInterfaceLanguage() || 'de';
}

const LinkEvaluation: React.FC = () => {
    const { slug, token } = useParams<{ slug: string; token: string }>();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const linkIsValidAccessPassRef = useRef<unknown>(null);
    const tempOrgId = sessionStorage.getItem('tempOrgId');
    const [orgId, setOrgId] = useState<string | null>(tempOrgId);
    const [accessName, setAccessName] = useState<string>('');
    const [slugAccessPassRaw, setSlugAccessPassRaw] = useState<unknown>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [branding] = useOrganizationBranding(orgId as string);
    const [interfaceLanguage, setInterfaceLanguage] = useState<UiLang>(getInitialInterfaceLanguage);

    const chosenLang = interfaceLanguage;

    // Memoize computed values
    const backgroundImage = useMemo(() => branding?.visitor_entrance_background_image, [branding]);
    const backgroundColor = useMemo(() => {
        if (branding?.orgColors) {
            return branding.orgColors.length > 1 ? `linear-gradient(45deg,${branding.orgColors.join(',')})` : branding.orgColors[0];
        }
        return IvicosColors.CampusGradient;
    }, [branding]);
    const customLogo = useMemo(() => branding?.orgLogo, [branding]);
    const textColor = useMemo(() => getTextColorForBackground(backgroundColor), [backgroundColor]);
    const campusName = useMemo(() => branding?.orgName, [branding]);
    const description = useMemo(
        () => (chosenLang === 'de' ? `Zugang zum Campus ${campusName}` : `Access to the Campus ${campusName}`),
        [chosenLang, accessName, campusName]
    );

    const baseUrl = process.env.REACT_APP_ACCESS_MANAGEMENT_URL;

    const allowedOrigin = useMemo(() => {
        if (!baseUrl) return null;
        try {
            return new URL(baseUrl).origin;
        } catch {
            return null;
        }
    }, [baseUrl]);

    const [linkEvaluationPayload, setLinkEvaluationPayload] = useState<ILinkEvaluationParentPayload | null>(null);

    /**
     * Link-evaluation iframe notifies parent via postMessage; origin must match REACT_APP_ACCESS_MANAGEMENT_URL.
     */
    useEffect(() => {
        if (!allowedOrigin) return;

        const handleMessage = (event: MessageEvent): void => {
            if (event.origin !== allowedOrigin) return;
            const { data } = event;
            if (isLinkEvaluationMessage(data)) {
                setLinkEvaluationPayload(data);
            } else if (isLinkIsValidMessage(data)) {
                linkIsValidAccessPassRef.current = data.data;
            }
        };

        window.addEventListener('message', handleMessage);
        return (): void => {
            window.removeEventListener('message', handleMessage);
        };
    }, [allowedOrigin]);

    const feazyFromAccessPass = useMemo(() => getFeazyEmbedFromSlugAccessPass(slugAccessPassRaw), [slugAccessPassRaw]);

    const iframeChatbot = linkEvaluationPayload?.chatbot;
    const iframeWantsAssistantWhenClosed = iframeChatbot?.showForClosedAccess === true;

    /** Iframe gates “assistant when closed”; prompt/base URL come only from slug access-pass API payload. */
    const shouldRenderFeazy = !!(iframeWantsAssistantWhenClosed && feazyFromAccessPass);
    const feazyEffectivePrompt = feazyFromAccessPass?.promptId ?? '';
    const feazyEffectiveBaseUrl = feazyFromAccessPass?.baseUrl || 'https://api.feazy.ai';

    // Memoize iframe URL
    const iframeUrl = useMemo(
        () => (baseUrl ? `${baseUrl}/link-evaluation?slug=${slug}&token=${token}&lang=${encodeURIComponent(chosenLang)}` : null),
        [baseUrl, slug, token, chosenLang]
    );

    // Fetch access pass data
    const fetchAccessPass = useCallback(async () => {
        if (!slug) return;
        try {
            setIsLoading(true);
            const res = await idpService.showAccessPassBySlug(slug);
            const accessPass = unwrapAccessPassPayload(res) as Record<string, unknown>;
            setSlugAccessPassRaw(res);
            setOrgId(optionalNonEmptyString(accessPass?.orgId) || optionalNonEmptyString(accessPass?.org_id) || null);
            setAccessName(optionalNonEmptyString(accessPass?.access_name) || optionalNonEmptyString(accessPass?.accessName) || '');
        } catch (error) {
            console.error('Error fetching access pass:', error);
        } finally {
            setIsLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchAccessPass();
    }, [fetchAccessPass]);

    // Memoize container styles
    const containerStyles = useMemo(
        () => ({
            width: '100vw',
            height: '100vh',
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            alignItems: 'center',
            justifyContent: 'center'
        }),
        [backgroundImage]
    );

    const iframeContainerStyles = useMemo(
        () => ({
            width: '90%',
            maxWidth: '1200px',
            height: '85%',
            maxHeight: '800px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden'
        }),
        []
    );

    if (!iframeUrl) {
        return (
            <Stack style={{ width: '100vw', height: '100vh' }} horizontalAlign="center" verticalAlign="center">
                <Text>Access Management URL not configured. Please set REACT_APP_ACCESS_MANAGEMENT_URL environment variable.</Text>
            </Stack>
        );
    }

    return (
        <Stack style={containerStyles}>
            <Stack style={iframeContainerStyles}>
                <Stack style={{ boxShadow: Depths.depth64 }}>
                    <Stack horizontal verticalAlign="center" style={{ background: backgroundColor }}>
                        <Stack
                            aria-label="visitor-landing-page-logo"
                            style={{
                                height: 100,
                                width: 100,
                                backgroundImage: `url(${customLogo || IvicosLogo})`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'left center',
                                margin: '8px 10px 8px 20px'
                            }}
                        />

                        <Stack
                            aria-label="visitor-landing-page-event-name"
                            verticalAlign="center"
                            tokens={{ childrenGap: 10 }}
                            style={{ width: 'calc(90% - 100px)', height: 100, padding: '10px' }}
                        >
                            {!isLoading && (
                                <Stack horizontal horizontalAlign="space-between">
                                    <Stack>
                                        <Text style={{ color: textColor, textAlign: 'center', fontWeight: 700 }} variant="xLarge">
                                            {description}
                                        </Text>
                                        <Text
                                            style={{
                                                lineHeight: '30px',
                                                color: textColor,
                                                width: '100%',
                                                wordBreak: 'break-word',
                                                fontWeight: 600
                                            }}
                                            variant="mediumPlus"
                                        >
                                            {accessName}
                                        </Text>
                                    </Stack>
                                    <Stack style={{ marginBottom: 20 }} tokens={{ childrenGap: 6 }} horizontal>
                                        <Stack
                                            horizontal
                                            verticalAlign="center"
                                            tokens={{ childrenGap: 8 }}
                                            role="group"
                                            aria-label="visitor-landing-page-interface-language"
                                        >
                                            <IC variant="custom" size={16} style={{ display: 'flex', alignItems: 'center' }}>
                                                <Languages />
                                            </IC>
                                            <ActionButton
                                                className="language-landing-button"
                                                styles={{
                                                    root: {
                                                        padding: 0,
                                                        minWidth: 'auto',
                                                        height: 'auto',
                                                        lineHeight: '20px',
                                                        fontSize: 14,
                                                        textDecoration: 'none',
                                                        fontWeight: interfaceLanguage === 'en' ? 'bold' : 'normal',
                                                        opacity: interfaceLanguage === 'en' ? 1 : 0.55,
                                                        cursor: 'pointer',
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    rootHovered: {
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    rootPressed: {
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    rootFocused: {
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    label: {
                                                        color: 'inherit'
                                                    },
                                                    labelHovered: {
                                                        color: 'inherit'
                                                    }
                                                }}
                                                onClick={() => setInterfaceLanguage('en')}
                                            >
                                                English
                                            </ActionButton>
                                            <Text style={{ lineHeight: '20px' }}>|</Text>
                                            <ActionButton
                                                className="language-landing-button"
                                                styles={{
                                                    root: {
                                                        padding: 0,
                                                        minWidth: 'auto',
                                                        height: 'auto',
                                                        lineHeight: '20px',
                                                        fontSize: 14,
                                                        textDecoration: 'none',
                                                        fontWeight: interfaceLanguage === 'de' ? 'bold' : 'normal',
                                                        opacity: interfaceLanguage === 'de' ? 1 : 0.55,
                                                        cursor: 'pointer',
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    rootHovered: {
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    rootPressed: {
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    rootFocused: {
                                                        backgroundColor: 'transparent',
                                                        color: 'inherit'
                                                    },
                                                    label: {
                                                        color: 'inherit'
                                                    },
                                                    labelHovered: {
                                                        color: 'inherit'
                                                    }
                                                }}
                                                onClick={() => setInterfaceLanguage('de')}
                                            >
                                                Deutsch
                                            </ActionButton>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </Stack>
                <iframe
                    ref={iframeRef}
                    src={iframeUrl}
                    frameBorder="0"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Link Evaluation"
                    aria-label="Link Evaluation iframe"
                    allow="clipboard-write"
                />
            </Stack>
            {shouldRenderFeazy && feazyEffectivePrompt && (
                <div style={{ position: 'fixed', bottom: 0, right: 0, zIndex: 9999 }}>
                    <FeazyChat promptId={feazyEffectivePrompt} baseUrl={feazyEffectiveBaseUrl} />
                </div>
            )}
        </Stack>
    );
};

export default LinkEvaluation;
